import clx from 'classnames';
import { ReactComponent as CloseIcon } from '../../../assets/svg/cross.svg';
import styles from './Modal.module.scss';
import ReactDOM from 'react-dom';
import { useCallback, useEffect, useRef, useState } from 'react';
import useWindowSize from '../../../utils/useWindowSize';
import {
  decrementModalChromeLock,
  getModalChromeLockCount,
  healInteractionState,
  incrementModalChromeLock,
} from '../../../utils/interactionRecovery';
import { armClickShield } from '../../../utils/armClickShield';
import { hideHubSpotForModal } from '../../../utils/hubspotChat';
import { getHtmlZoom } from '../../../utils/lockWebViewTextZoom';

export { getModalChromeLockCount };

const DISMISS_DISTANCE = 110;
const DISMISS_VELOCITY = 0.85;
const CLOSE_MS = 220;
const DRAG_ARM_PX = 10;
const INTERACTIVE_SELECTOR = 'button, a, input, textarea, select, label, [role="button"]';

const RenderModal = (props) => {
  return ReactDOM.createPortal(props.children, document.getElementById('modal-root'));
};

const Modal = ({
  isModalOpen,
  setIsModalOpen,
  children,
  height = 'auto',
  width = '',
  maxHeight = 'auto',
  maxWidth = '100%',
  showCloseBtn = false,
  isError = false,
  title = '',
  subTitle = '',
  borderRadius = '20px',
  fullScreen = false,
  centered = false,
  dark = false,
  closeOnOverlayClick = true,
}) => {
  const { isMobile, isTablet } = useWindowSize();
  const isSheet = Boolean((isMobile || isTablet) && !isError && !centered);
  /** Error e centered: mai slide da destra. */
  const isCentered = Boolean(centered || isError);
  const sheetRef = useRef(null);
  const contentRef = useRef(null);
  const dragYRef = useRef(0);
  const isDraggingRef = useRef(false);
  const closingLockRef = useRef(false);
  const closeTimerRef = useRef(null);
  const closeModalRef = useRef(() => {});
  /** Questa istanza ha acquisito il lock #root (StrictMode-safe). */
  const holdsChromeLockRef = useRef(false);
  const disposeClickShieldRef = useRef(null);
  const dragRef = useRef({
    active: false,
    armed: false,
    startY: 0,
    startX: 0,
    lastY: 0,
    lastTs: 0,
    velocity: 0,
    fromHeader: false,
  });
  const [isDragging, setIsDragging] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [sheetReady, setSheetReady] = useState(false);

  const releaseChromeLock = useCallback(() => {
    if (!holdsChromeLockRef.current) return;
    holdsChromeLockRef.current = false;
    const prev = getModalChromeLockCount();
    if (prev <= 0) {
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.warn('[Modal] chrome lock count mismatch on release', prev);
      }
      healInteractionState({ reason: 'modal-mismatch' });
      return;
    }
    const next = decrementModalChromeLock();
    if (next <= 0) {
      healInteractionState({ reason: 'modal-last-close' });
    }
  }, []);

  const acquireChromeLock = useCallback(() => {
    if (holdsChromeLockRef.current) return;
    const root = document.getElementById('root');
    if (root?.style.pointerEvents === 'none' && getModalChromeLockCount() <= 0) {
      root.style.pointerEvents = '';
      root.removeAttribute('aria-hidden');
    }
    holdsChromeLockRef.current = true;
    incrementModalChromeLock();
  }, []);

  const setDragOffset = useCallback((nextY, dragging) => {
    dragYRef.current = nextY;
    const wasDragging = isDraggingRef.current;
    isDraggingRef.current = dragging;

    const node = sheetRef.current;
    if (node && isSheet) {
      if (dragging) {
        node.style.transition = 'none';
        node.style.animation = 'none';
        node.style.transform = `translate3d(0, ${Math.max(nextY, 0)}px, 0)`;
      } else if (!closingLockRef.current) {
        node.style.transition = `transform ${CLOSE_MS}ms cubic-bezier(0.2, 0.9, 0.2, 1)`;
        node.style.transform = 'translate3d(0, 0, 0)';
      }
    }

    // Evita re-render a ogni pixel: aggiorna React solo quando inizia/finisce il drag.
    if (wasDragging !== dragging) {
      setIsDragging(dragging);
    }
  }, [isSheet]);

  const resetDrag = useCallback(() => {
    dragRef.current.active = false;
    dragRef.current.armed = false;
    dragRef.current.fromHeader = false;
    setDragOffset(0, false);
  }, [setDragOffset]);

  const closeModal = useCallback(
    (event) => {
      event?.preventDefault?.();
      event?.stopPropagation?.();
      if (closingLockRef.current) return;
      closingLockRef.current = true;
      dragRef.current.active = false;
      dragRef.current.armed = false;
      disposeClickShieldRef.current?.();
      disposeClickShieldRef.current = armClickShield();

      if (isSheet) {
        setIsClosing(true);
        setDragOffset(0, false);
        if (closeTimerRef.current) window.clearTimeout(closeTimerRef.current);
        closeTimerRef.current = window.setTimeout(() => {
          setIsModalOpen(false);
          closingLockRef.current = false;
          setIsClosing(false);
          setSheetReady(false);
          closeTimerRef.current = null;
        }, CLOSE_MS);
        return;
      }

      setIsModalOpen(false);
      closingLockRef.current = false;
    },
    [isSheet, setIsModalOpen, setDragOffset],
  );

  closeModalRef.current = closeModal;

  useEffect(
    () => () => {
      if (closeTimerRef.current) window.clearTimeout(closeTimerRef.current);
      disposeClickShieldRef.current?.();
      disposeClickShieldRef.current = null;
      // Unmount: rilascia sempre il lock di questa istanza
      releaseChromeLock();
    },
    [releaseChromeLock],
  );

  // Overflow body + count; il blocco tap è l’overlay full-screen, non #root.
  useEffect(() => {
    if (!isModalOpen) {
      releaseChromeLock();
      if (getModalChromeLockCount() <= 0) {
        healInteractionState({ reason: 'modal-closed' });
      }
      return undefined;
    }

    acquireChromeLock();
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const releaseHubSpot = hideHubSpotForModal();

    return () => {
      releaseHubSpot();
      releaseChromeLock();
      document.body.style.overflow = prevOverflow;
      if (getModalChromeLockCount() <= 0) {
        healInteractionState({ reason: 'modal-cleanup' });
      }
    };
  }, [isModalOpen, acquireChromeLock, releaseChromeLock]);

  useEffect(() => {
    if (!isModalOpen) {
      closingLockRef.current = false;
      setIsClosing(false);
      setSheetReady(false);
      setDragOffset(0, false);
      return undefined;
    }
    if (!isSheet) return undefined;
    const timer = window.setTimeout(() => setSheetReady(true), 220);
    return () => window.clearTimeout(timer);
  }, [isModalOpen, isSheet, setDragOffset]);

  useEffect(() => {
    if (!isModalOpen || !isSheet) return undefined;

    const onTouchStart = (event) => {
      if (closingLockRef.current || event.touches.length !== 1) return;
      const target = event.target;
      if (target?.closest?.(INTERACTIVE_SELECTOR)) {
        dragRef.current.active = false;
        return;
      }

      // Dismiss solo da handle / header: lo scroll del body non deve chiudere lo sheet.
      const onHeader = Boolean(target?.closest?.('[data-sheet-handle]'));
      if (!onHeader) {
        dragRef.current.active = false;
        return;
      }

      const touch = event.touches[0];
      dragRef.current = {
        active: true,
        armed: false,
        startY: touch.clientY,
        startX: touch.clientX,
        lastY: touch.clientY,
        lastTs: event.timeStamp,
        velocity: 0,
        fromHeader: true,
      };
    };

    const onTouchMove = (event) => {
      const state = dragRef.current;
      if (!state.active || !state.fromHeader || closingLockRef.current) return;
      const touch = event.touches[0];
      const dy = touch.clientY - state.startY;
      const dx = touch.clientX - state.startX;
      const dt = Math.max(event.timeStamp - state.lastTs, 1);
      state.velocity = (touch.clientY - state.lastY) / dt;
      state.lastY = touch.clientY;
      state.lastTs = event.timeStamp;

      // Gesto orizzontale → non dismiss
      if (!state.armed && Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > DRAG_ARM_PX) {
        state.active = false;
        return;
      }

      if (dy <= 0) {
        if (isDraggingRef.current) setDragOffset(0, false);
        state.armed = false;
        return;
      }

      if (!state.armed) {
        if (dy < DRAG_ARM_PX) return;
        state.armed = true;
      }

      if (event.cancelable) event.preventDefault();
      setDragOffset(dy, true);
    };

    const onTouchEnd = () => {
      const state = dragRef.current;
      if (!state.active) return;
      const currentY = dragYRef.current;
      const shouldClose =
        state.armed &&
        (currentY >= DISMISS_DISTANCE || (currentY > 24 && state.velocity >= DISMISS_VELOCITY));
      state.active = false;
      state.armed = false;

      if (shouldClose) {
        closeModalRef.current();
        return;
      }
      resetDrag();
    };

    const node = sheetRef.current;
    if (!node) return undefined;

    node.addEventListener('touchstart', onTouchStart, { passive: true });
    node.addEventListener('touchmove', onTouchMove, { passive: false });
    node.addEventListener('touchend', onTouchEnd);
    node.addEventListener('touchcancel', onTouchEnd);

    return () => {
      node.removeEventListener('touchstart', onTouchStart);
      node.removeEventListener('touchmove', onTouchMove);
      node.removeEventListener('touchend', onTouchEnd);
      node.removeEventListener('touchcancel', onTouchEnd);
    };
  }, [isModalOpen, isSheet, resetDrag, setDragOffset]);

  // Side panel desktop/tablet: 100dvh può superare visualViewport (Safari/WebView) e tagliare il footer.
  useEffect(() => {
    if (!isModalOpen || isSheet) return undefined;
    const node = sheetRef.current;
    if (!node) return undefined;
    const layoutH = Math.round(
      (window.visualViewport?.height || window.innerHeight || 0) / getHtmlZoom(),
    );
    if (layoutH > 0) {
      node.style.setProperty('--modal-vvh', `${layoutH}px`);
    }
    return () => {
      node.style.removeProperty('--modal-vvh');
    };
  }, [isModalOpen, isSheet]);

  // Sheet: altezza = area visibile. Con tastiera (Android/Oppo) visualViewport si
  // restringe: senza sync il campo resta sopra lo schermo e si vede solo Conferma.
  useEffect(() => {
    if (!isModalOpen || !isSheet) return undefined;
    const sheet = sheetRef.current;
    if (!sheet) return undefined;
    const wrapper = sheet.parentElement;

    let pinnedEl = null;
    let focusTimer = null;
    let layoutTimer = null;

    const clearPin = () => {
      if (pinnedEl) {
        pinnedEl.removeAttribute('data-keyboard-pin');
        pinnedEl = null;
      }
    };

    const pinField = (target) => {
      const field =
        target.closest('[data-keyboard-pin-root]') ||
        target.closest('label') ||
        target;
      if (!(field instanceof HTMLElement)) return;
      if (pinnedEl === field) return;
      clearPin();
      pinnedEl = field;
      pinnedEl.setAttribute('data-keyboard-pin', 'true');
    };

    const isEditable = (node) => {
      if (!(node instanceof HTMLElement)) return false;
      const tag = node.tagName;
      return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
    };

    const scrollFocusedIntoView = () => {
      const active = document.activeElement;
      if (!isEditable(active) || !sheet.contains(active)) return;
      const pin =
        active.closest('[data-keyboard-pin-root]') ||
        active.closest('label') ||
        active;
      if (!(pin instanceof HTMLElement)) return;
      pin.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'auto' });
    };

    const applyViewport = () => {
      const vv = window.visualViewport;
      const cssZoom = getHtmlZoom();
      const vvH = Math.round((vv?.height || window.innerHeight || 0) / cssZoom);
      const vvTop = Math.round((vv?.offsetTop || 0) / cssZoom);
      const layoutH = Math.round((window.innerHeight || 0) / cssZoom);
      const overlap = Math.max(0, layoutH - vvTop - vvH);
      const focused = isEditable(document.activeElement) && sheet.contains(document.activeElement);
      // Android/Oppo: innerHeight può già essere ristretto (overlap≈0) oppure la tastiera
      // copre senza resize. Con focus trattiamo sempre lo sheet come “tastiera aperta”.
      const keyboardOpen = focused;

      if (keyboardOpen) {
        sheet.dataset.keyboard = 'true';
        const visibleH = Math.max(240, overlap > 60 ? vvH : Math.min(vvH, layoutH));
        sheet.style.setProperty('--sheet-vvh', `${visibleH}px`);
        if (wrapper) {
          wrapper.style.paddingBottom = overlap > 0 ? `${overlap}px` : '';
        }
      } else {
        sheet.dataset.keyboard = 'false';
        sheet.style.setProperty('--sheet-vvh', `${Math.round(Math.min(vvH * 0.92, vvH))}px`);
        wrapper?.style.removeProperty('padding-bottom');
        if (!focused) clearPin();
      }
    };

    const onViewportChange = () => {
      applyViewport();
      if (layoutTimer) window.clearTimeout(layoutTimer);
      layoutTimer = window.setTimeout(scrollFocusedIntoView, 80);
    };

    applyViewport();
    sheet.style.setProperty('--form-min-height', '0px');
    wrapper?.style.removeProperty('--keyboard-offset');

    const onFocusIn = (event) => {
      const target = event.target;
      if (!isEditable(target) || !sheet.contains(target)) return;
      pinField(target);
      applyViewport();
      if (focusTimer) window.clearTimeout(focusTimer);
      focusTimer = window.setTimeout(() => {
        applyViewport();
        scrollFocusedIntoView();
      }, 280);
    };

    const onFocusOut = (event) => {
      const next = event.relatedTarget;
      if (next instanceof Node && sheet.contains(next)) return;
      window.setTimeout(() => {
        if (!sheet.contains(document.activeElement) || !isEditable(document.activeElement)) {
          clearPin();
          applyViewport();
        }
      }, 0);
    };

    sheet.addEventListener('focusin', onFocusIn, true);
    sheet.addEventListener('focusout', onFocusOut, true);
    window.visualViewport?.addEventListener('resize', onViewportChange);
    window.visualViewport?.addEventListener('scroll', onViewportChange);
    window.addEventListener('resize', onViewportChange);

    return () => {
      sheet.removeEventListener('focusin', onFocusIn, true);
      sheet.removeEventListener('focusout', onFocusOut, true);
      window.visualViewport?.removeEventListener('resize', onViewportChange);
      window.visualViewport?.removeEventListener('scroll', onViewportChange);
      window.removeEventListener('resize', onViewportChange);
      if (focusTimer) window.clearTimeout(focusTimer);
      if (layoutTimer) window.clearTimeout(layoutTimer);
      clearPin();
      sheet.style.removeProperty('--sheet-vvh');
      sheet.style.removeProperty('--form-min-height');
      delete sheet.dataset.keyboard;
      wrapper?.style.removeProperty('--keyboard-offset');
      wrapper?.style.removeProperty('padding-bottom');
    };
  }, [isModalOpen, isSheet]);

  return (
    <>
      {!!isModalOpen && (
        <RenderModal>
          <div
            className={clx(styles['modal__wrapper'], {
              [styles.is_error]: isError,
              [styles.modal__wrapper_centered]: isCentered,
              [styles.modal__wrapper_sheet]: isSheet,
              [styles.modal__wrapper_closing]: isClosing,
            })}
            data-modal-open="true"
            onClick={closeOnOverlayClick ? closeModal : undefined}
          >
            <div
              ref={sheetRef}
              className={clx(styles['modal'], {
                [styles.modal_mobile]: isSheet,
                [styles.modal_fullscreen]: fullScreen && !isMobile,
                [styles.modal_centered]: isCentered,
                [styles.modal_dark]: dark,
                [styles.modal_mobile_dragging]: isSheet && isDragging,
                [styles.modal_mobile_closing]: isSheet && isClosing,
              })}
              style={{
                width,
                maxWidth,
                // Sheet: altezza solo da CSS (--sheet-vvh). Inline `auto` su Safari vince sul flex e taglia il footer.
                ...(isSheet
                  ? null
                  : {
                      height: fullScreen ? (isMobile ? '100dvh' : '100vh') : height,
                      maxHeight: isCentered
                        ? maxHeight === 'auto'
                          ? '90vh'
                          : maxHeight
                        : maxHeight,
                    }),
                borderRadius: fullScreen ? '0px' : borderRadius,
                ...(isSheet && (sheetReady || isClosing) && !isDragging
                  ? {
                      transform: isClosing
                        ? 'translate3d(0, 110%, 0)'
                        : 'translate3d(0, 0, 0)',
                      transition: `transform ${CLOSE_MS}ms cubic-bezier(0.2, 0.9, 0.2, 1)`,
                      animation: 'none',
                    }
                  : null),
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {isSheet ? (
                <div className={styles.sheet_handle} data-sheet-handle aria-hidden>
                  <span className={styles.sheet_handle_bar} />
                </div>
              ) : null}

              {title || showCloseBtn ? (
                <div className={styles.modal__header_wrapper} data-sheet-handle data-sheet-header>
                  <div className={styles.modal__header}>
                    <div className={styles.modal__title}>
                      {title}
                      <span className={styles.modal__title_bold}>{subTitle}</span>
                    </div>

                    {showCloseBtn && (
                      <button
                        type="button"
                        className={styles.close_button}
                        aria-label="Chiudi"
                        onPointerDown={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          closeModal(event);
                        }}
                        onClick={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                        }}
                      >
                        <CloseIcon aria-hidden />
                      </button>
                    )}
                  </div>
                </div>
              ) : null}
              <div ref={contentRef} className={styles.modal__content} data-modal-scroll>
                {children}
              </div>
            </div>
          </div>
        </RenderModal>
      )}
    </>
  );
};

export default Modal;
